// Vendor Program v2 PR 2 — staff surfaces over monthly vendor reports
// (review queue + drawer), expected-vs-filed compliance chips, performance
// rollups, the complaints triage module, and the loan↔borrower portal-link
// affordance. Mirrors PhoenixCapital.Core DTOs VendorReportDtos /
// VendorComplaintDtos / BorrowerVendorProgramDtos exactly. Reads gated
// Vendors.Read, mutations Vendors.Manage (void is ADMIN-gated in-body).

import { api } from "@/api/client";

export type VendorReportStatus =
  | "draft"
  | "submitted"
  | "returned"
  | "accepted"
  | "signed-off"
  | "void";

export interface VendorReportGoalGradeDto {
  goalId: string;
  goalTitle: string;
  completionCriteria: string | null;
  targetMonths: string | null;
  score: number;
  isComplete: boolean;
  note: string | null;
}

/** F11 regulated (borrower-paid) §4 line — Private to Phoenix, like the
 *  invoice fields it replaces. */
export interface VendorReportCollectedLineItemDto {
  id: string;
  amount: number;
  serviceDescription: string;
}

export interface VendorMonthlyReportDto {
  id: string;
  assignmentId: string;
  loanId: string;
  loanNumber: string;
  vendorId: string;
  vendorName: string;
  roleOnLoan: string;
  periodStart: string;
  periodEnd: string;
  status: VendorReportStatus;
  revisionNumber: number;
  /** §1 — shared with the borrower. */
  publicNarrative: string | null;
  /** §2 — shared with the borrower. */
  goalGrades: VendorReportGoalGradeDto[];
  /** §3 — Private to Phoenix; never reaches the borrower DTOs. */
  privateConcerns: string | null;
  /** §4 — Private to Phoenix. */
  selfReportedInvoiceAmount: number | null;
  amountCalculationExplanation: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  signedOffAt: string | null;
  latestReviewComment: string | null;
  // F11 regulated (borrower-paid) §4 variant — optional so pre-existing
  // fixtures stay valid; the server always sends them.
  isRegulatedVendor?: boolean;
  collectedLineItems?: VendorReportCollectedLineItemDto[] | null;
  collectedNothingConfirmed?: boolean;
  // SP-4 WS-2 — the §4 quantity (hours / transaction value) + the vendor's
  // effective contract-term basis.
  reportedQuantity?: number | null;
  contractTermBasis?: string | null;
}

export interface VendorReportRevisionDto {
  revisionNumber: number;
  submittedAt: string;
  reviewedAt: string | null;
  reviewOutcome: string | null;
  reviewComment: string | null;
}

export interface VendorReportPriorAmountDto {
  periodStart: string;
  amount: number | null;
}

export interface VendorReportSignOffDto {
  revisionNumber: number;
  signedAt: string;
  serviceQuality: number;
  responsiveness: number;
  satisfaction: number;
  overallRating: number;
  feedback: string | null;
  /** True when Phoenix recorded this on the borrower's behalf. The rating is
   *  the borrower's either way — this says who entered it, so a transcribed
   *  sign-off never reads as the borrower's own submission. */
  isStaffRecorded: boolean;
  /** The staff member who recorded it; null for a borrower-submitted one. */
  recordedByName: string | null;
}

export interface VendorReportReviewDto {
  report: VendorMonthlyReportDto;
  revisions: VendorReportRevisionDto[];
  priorAmounts: VendorReportPriorAmountDto[];
  signOff: VendorReportSignOffDto | null;
}

export interface VendorReportOutstandingDto {
  assignmentId: string;
  loanId: string;
  loanNumber: string;
  vendorId: string;
  vendorName: string;
  roleOnLoan: string;
  periodStart: string;
  status: "missing" | "draft" | "returned";
}

export interface VendorReportComplianceDto {
  expectedCount: number;
  filedCount: number;
  outstanding: VendorReportOutstandingDto[];
}

export interface VendorPerformanceAreaDto {
  overall: number | null;
  serviceQuality: number | null;
  responsiveness: number | null;
  satisfaction: number | null;
  signOffCount: number;
  /** How many of `signOffCount` were recorded by staff. They count toward the
   *  averages; a blended number with no such marker would present
   *  Phoenix-entered ratings as the borrower's own. */
  staffRecordedCount: number;
}

/** One line of the loan Vendors tab's program-history card. */
export interface LoanVendorHistoryEntryDto {
  id: string;
  occurredAt: string;
  /** "assigned" | "updated" | "removed" — derived from the audited action. */
  kind: string;
  summary: string;
  vendorId: string | null;
  vendorName: string | null;
  roleOnLoan: string | null;
  actorDisplayName: string | null;
}

export interface VendorPerformanceTrendPointDto {
  month: string;
  overall: number;
  count: number;
}

export interface VendorPerformanceCustomerDto {
  loanId: string;
  loanNumber: string;
  roleOnLoan: string;
  ratings: VendorPerformanceAreaDto;
}

export interface VendorPerformanceFeedbackDto {
  periodStart: string;
  loanNumber: string;
  overallRating: number;
  feedback: string;
}

export interface VendorPerformanceSummaryDto {
  overall: VendorPerformanceAreaDto;
  monthlyTrend: VendorPerformanceTrendPointDto[];
  perCustomer: VendorPerformanceCustomerDto[];
  recentFeedback: VendorPerformanceFeedbackDto[];
}

export interface LoanBorrowerLinkDto {
  portalUserId: string;
  email: string;
  fullName: string;
  source: string;
  createdAt: string;
}

/** Seller portal-account link — same shape as the borrower link (a loan can
 *  have several sellers, so this is a list). */
export interface LoanSellerLinkDto {
  portalUserId: string;
  email: string;
  fullName: string;
  source: string;
  createdAt: string;
}

/** Broker portal-account link — a single resolved account (not a list); all
 *  fields null when no broker account is linked. */
export interface LoanBrokerLinkDto {
  portalUserId: string | null;
  email: string | null;
  fullName: string | null;
}

export type VendorComplaintStatus =
  | "new"
  | "acknowledged"
  | "resolved"
  | "handed-off";

export interface VendorComplaintDto {
  id: string;
  reportId: string | null;
  assignmentId: string;
  loanId: string;
  loanNumber: string;
  vendorId: string;
  vendorName: string;
  text: string;
  status: VendorComplaintStatus;
  createdAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  resolvedReason: string | null;
  complianceIntakeReportId: string | null;
}

export interface VendorReportListParams {
  loanId?: string;
  vendorId?: string;
  status?: VendorReportStatus;
}

export interface VendorReportComplianceParams {
  loanId?: string;
  vendorId?: string;
}

export const vendorReportsApi = {
  list: (params?: VendorReportListParams) =>
    api.get<VendorMonthlyReportDto[]>("/portfolio-admin/vendor-reports", {
      loanId: params?.loanId,
      vendorId: params?.vendorId,
      status: params?.status,
    }),
  review: (reportId: string) =>
    api.get<VendorReportReviewDto>(
      `/portfolio-admin/vendor-reports/${reportId}/review`,
    ),
  return: (reportId: string, body: { comment: string }) =>
    api.post<VendorMonthlyReportDto>(
      `/portfolio-admin/vendor-reports/${reportId}/return`,
      body,
    ),
  accept: (reportId: string) =>
    api.post<VendorMonthlyReportDto>(
      `/portfolio-admin/vendor-reports/${reportId}/accept`,
    ),
  /** Records the borrower's sign-off on their behalf — same star inputs and
   *  same accepted-only precondition as the portal path. Vendors.Manage. */
  recordSignOff: (
    reportId: string,
    body: {
      serviceQuality: number;
      responsiveness: number;
      satisfaction: number;
      feedback?: string | null;
    },
  ) =>
    api.post<VendorMonthlyReportDto>(
      `/portfolio-admin/vendor-reports/${reportId}/record-sign-off`,
      body,
    ),
  /** ADMIN-gated server-side (role checked in-body, CorrectStatus precedent). */
  void: (reportId: string, body: { reason: string }) =>
    api.post<VendorMonthlyReportDto>(
      `/portfolio-admin/vendor-reports/${reportId}/void`,
      body,
    ),
  /** Step 3 W5 — loan vendor-assignment history, newest first. */
  vendorHistory: (loanId: string) =>
    api.get<LoanVendorHistoryEntryDto[]>(
      `/portfolio-admin/loans/${loanId}/vendor-history`,
    ),
  compliance: (params?: VendorReportComplianceParams) =>
    api.get<VendorReportComplianceDto>(
      "/portfolio-admin/vendor-report-compliance",
      { loanId: params?.loanId, vendorId: params?.vendorId },
    ),
  performance: (vendorId: string) =>
    api.get<VendorPerformanceSummaryDto>(
      `/portfolio-admin/vendors/${vendorId}/performance`,
    ),
  borrowerLinks: (loanId: string) =>
    api.get<LoanBorrowerLinkDto[]>(
      `/portfolio-admin/loans/${loanId}/borrower-links`,
    ),
  linkBorrower: (loanId: string, portalUserId: string) =>
    api.post<LoanBorrowerLinkDto[]>(
      `/portfolio-admin/loans/${loanId}/borrower-links`,
      { portalUserId },
    ),
  sellerLinks: (loanId: string) =>
    api.get<LoanSellerLinkDto[]>(
      `/portfolio-admin/loans/${loanId}/seller-links`,
    ),
  linkSeller: (loanId: string, portalUserId: string) =>
    api.post<LoanSellerLinkDto[]>(
      `/portfolio-admin/loans/${loanId}/seller-links`,
      { portalUserId },
    ),
  brokerLink: (loanId: string) =>
    api.get<LoanBrokerLinkDto | null>(
      `/portfolio-admin/loans/${loanId}/broker-link`,
    ),
  linkBroker: (loanId: string, portalUserId: string) =>
    api.post<LoanBrokerLinkDto>(
      `/portfolio-admin/loans/${loanId}/broker-link`,
      { portalUserId },
    ),
};

export const vendorComplaintsApi = {
  list: (status?: VendorComplaintStatus) =>
    api.get<VendorComplaintDto[]>("/portfolio-admin/complaints", { status }),
  /** Single fetch so the ?complaint= deep link works regardless of the
   *  queue's active status filter. */
  get: (complaintId: string) =>
    api.get<VendorComplaintDto>(`/portfolio-admin/complaints/${complaintId}`),
  acknowledge: (complaintId: string) =>
    api.post<VendorComplaintDto>(
      `/portfolio-admin/complaints/${complaintId}/acknowledge`,
    ),
  resolve: (complaintId: string, body: { reason: string }) =>
    api.post<VendorComplaintDto>(
      `/portfolio-admin/complaints/${complaintId}/resolve`,
      body,
    ),
  handoff: (complaintId: string, body: { suggestedSeverity?: string | null }) =>
    api.post<VendorComplaintDto>(
      `/portfolio-admin/complaints/${complaintId}/handoff`,
      body,
    ),
};
